import { useCallback, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import { describeError } from '@/api/errors';
import { useCommand } from '@/app/commands';
import { orgKey } from '@/app/queryClient';
import { useOrgId } from '@/app/session';
import { Button, Drawer, Field, IdChip, Modal, QueryState, TextArea, useContextMenu, useToast, type ContextMenuApi, type ContextMenuEntry } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import { addressesOf, conversationKeys, formatAddress, rawMessageQuery, type Conversation, type Location, type Message } from './api';

type Dialog = 'handoff' | 'details' | 'raw' | null;

export interface ConversationActions {
  menu: ContextMenuApi;
  /** Open, Mark read/unread, Close/Reopen, Archive, Copy link; `open: false` inside the thread itself. */
  items: (c: Conversation, opts?: { open?: boolean }) => ContextMenuEntry[];
  /** The menu popup and the close/reopen dialog; render once. */
  element: ReactNode;
}

/** The actions on one conversation, shared by the list rows (right click) and the thread's "..." button. */
export function useConversationActions(): ConversationActions {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { conversationId: openId } = useParams({ strict: false }) as { conversationId?: string };
  const menu = useContextMenu();
  const [lifecycle, setLifecycle] = useState<{ c: Conversation; reopen: boolean } | null>(null);

  const run = useCallback(
    async (c: Conversation, done: string, failed: string, fn: () => Promise<unknown>) => {
      try {
        await fn();
        await Promise.all(conversationKeys(orgId, c.id).map((k) => qc.invalidateQueries({ queryKey: k })));
        toast.show({ title: done });
        return true;
      } catch (e) {
        toast.show({ tone: 'accent', title: failed, body: describeError(e).body, durationMs: 10_000 });
        return false;
      }
    },
    [orgId, qc, toast],
  );

  /** Soft delete with Undo: the row leaves every loaded list at once; Undo restores it. */
  const remove = useCallback(
    async (c: Conversation) => {
      const listKey = orgKey(orgId, 'conversations', 'list');
      const refresh = () => Promise.all(conversationKeys(orgId, c.id).map((k) => qc.invalidateQueries({ queryKey: k })));
      const without = (page: unknown) =>
        page && typeof page === 'object' && Array.isArray((page as { items?: unknown }).items)
          ? { ...page, items: (page as { items: Conversation[] }).items.filter((x) => x.id !== c.id) }
          : page;
      await qc.cancelQueries({ queryKey: listKey });
      qc.setQueriesData({ queryKey: listKey }, (d: unknown) =>
        d && typeof d === 'object' && Array.isArray((d as { pages?: unknown }).pages)
          ? { ...d, pages: (d as { pages: unknown[] }).pages.map(without) }
          : without(d),
      );
      if (openId === c.id) void navigate({ to: '/app/conversations', search: true });
      try {
        const deleted = await unwrap(
          api.DELETE('/api/v1/orgs/{orgId}/conversations/{conversationId}', {
            params: { path: { orgId, conversationId: c.id }, header: { 'If-Match': ifMatch(c.version), 'Idempotency-Key': newIdempotencyKey() } },
            body: { reason: 'Deleted from the conversation list' },
          }),
        );
        void refresh();
        toast.show({
          title: 'Conversation deleted',
          durationMs: 10_000,
          action: {
            label: 'Undo',
            onAction: () =>
              void run(c, 'Conversation restored', 'Not restored', () =>
                unwrap(
                  api.POST('/api/v1/orgs/{orgId}/conversations/{conversationId}/restore', {
                    params: { path: { orgId, conversationId: c.id }, header: { 'If-Match': ifMatch(deleted.version), 'Idempotency-Key': newIdempotencyKey() } },
                    body: { reason: 'Undo delete' },
                  }),
                ),
              ),
          },
        });
      } catch (e) {
        void refresh();
        toast.show({ tone: 'accent', title: 'Not deleted', body: describeError(e).body, durationMs: 10_000 });
      }
    },
    [orgId, qc, toast, navigate, openId, run],
  );

  const items = useCallback(
    (c: Conversation, opts?: { open?: boolean }): ContextMenuEntry[] => {
      if (c.origin === 'source_recorded') return opts?.open === false ? [] : [{ label: 'Open recorded history', icon: 'chevronRight', onSelect: () => void navigate({ to: '/app/conversations/$conversationId', params: { conversationId: c.id }, search: true }) }];
      const path = { orgId, conversationId: c.id };
      const closed = c.state === 'closed' || c.state === 'archived';
      return [
        opts?.open === false
          ? 'separator'
          : { label: 'Open', icon: 'chevronRight', onSelect: () => void navigate({ to: '/app/conversations/$conversationId', params: { conversationId: c.id }, search: true }) },
        c.unread_count > 0
          ? {
              label: 'Mark read',
              icon: 'mail',
              // Listing the messages marks the conversation read.
              onSelect: () =>
                void run(c, 'Marked read', 'Not marked read', () =>
                  unwrap(api.GET('/api/v1/orgs/{orgId}/conversations/{conversationId}/messages', { params: { path, query: { sort: 'occurred_at', limit: 1 } } })),
                ),
            }
          : {
              label: 'Mark unread',
              icon: 'mail',
              onSelect: () =>
                void run(c, 'Marked unread', 'Not marked unread', () =>
                  unwrap(api.POST('/api/v1/orgs/{orgId}/conversations/{conversationId}/unread', { params: { path, header: { 'Idempotency-Key': newIdempotencyKey() } } })),
                ).then((ok) => {
                  // The open thread marks itself read again; step back to the list.
                  if (ok && openId === c.id) void navigate({ to: '/app/conversations', search: true });
                }),
            },
        closed
          ? { label: 'Reopen', icon: 'refresh', onSelect: () => setLifecycle({ c, reopen: true }), disabled: c.state === 'archived', hint: c.state === 'archived' ? 'Archived' : undefined }
          : { label: 'Close', icon: 'check', onSelect: () => setLifecycle({ c, reopen: false }) },
        {
          label: 'Archive',
          icon: 'inbox',
          disabled: c.state === 'archived',
          onSelect: () =>
            void run(c, 'Conversation archived', 'Not archived', () =>
              unwrap(
                api.POST('/api/v1/orgs/{orgId}/conversations/{conversationId}/archive', {
                  params: { path, header: { 'If-Match': ifMatch(c.version), 'Idempotency-Key': newIdempotencyKey() } },
                }),
              ),
            ),
        },
        {
          label: 'Copy link',
          icon: 'link',
          onSelect: () =>
            void navigator.clipboard.writeText(`${window.location.origin}/app/conversations/${c.id}`).then(
              () => toast.show({ title: 'Link copied' }),
              () => toast.show({ tone: 'accent', title: 'Link not copied' }),
            ),
        },
        { label: 'Delete conversation', icon: 'x', danger: true, onSelect: () => void remove(c) },
      ];
    },
    [orgId, navigate, openId, run, toast, remove],
  );

  const element = (
    <>
      {menu.element}
      {lifecycle ? <LifecycleModal key={lifecycle.c.id} conversation={lifecycle.c} reopen={lifecycle.reopen} onClose={() => setLifecycle(null)} /> : null}
    </>
  );
  return { menu, items, element };
}

const ORIGIN: Record<Message['record_origin'], string> = {
  platform: 'Eryce',
  mailbox_capture: 'Mailbox capture',
  imported_history: 'Imported history',
  manual_entry: 'Manual entry',
  source_recorded: 'Recorded source history',
};

/** The "..." menu of a thread: the shared actions, then hand off, parallel thread and the record behind the mail. */
export function ConversationMenu({
  conversation: c,
  location,
  messages,
}: {
  conversation: Conversation;
  location: Location | undefined;
  messages: Message[];
}) {
  const navigate = useNavigate({ from: '/app/conversations/$conversationId/' });
  const actions = useConversationActions();
  const [dialog, setDialog] = useState<Dialog>(null);
  const close = () => setDialog(null);
  const latest = messages.at(-1);

  const items = (): ContextMenuEntry[] => [
    ...actions.items(c, { open: false }),
    'separator',
    {
      label: 'Hand off',
      icon: 'user',
      onSelect: () => setDialog('handoff'),
      disabled: !location || location.policy.handoff_state === 'manual_only',
      hint: location?.policy.handoff_state === 'manual_only' ? 'Manual only' : undefined,
    },
    {
      label: 'New conversation on site',
      icon: 'plus',
      onSelect: () =>
        void navigate({ to: '/app/conversations/new', search: (s) => ({ ...s, location_id: c.primary_location_id ?? undefined }) }),
    },
    'separator',
    { label: 'Message details', icon: 'info', onSelect: () => setDialog('details'), disabled: messages.length === 0 },
    { label: 'Raw message', icon: 'file', onSelect: () => setDialog('raw'), disabled: !latest?.has_raw_mime },
  ];

  return (
    <>
      <Button
        variant="quiet"
        size="sm"
        icon="more"
        iconOnly
        aria-label="Conversation actions"
        aria-expanded={actions.menu.openKey === c.id}
        {...actions.menu.trigger(c.id, 'Conversation actions', items)}
      />
      {actions.element}
      {dialog === 'handoff' && location ? <HandoffModal location={location} onClose={close} /> : null}
      <Drawer open={dialog === 'details'} onClose={close} title="Message details" description="Source per message">
        {dialog === 'details' ? <MessageDetails messages={messages} /> : null}
      </Drawer>
      <Drawer open={dialog === 'raw'} onClose={close} title="Raw message" description={latest ? `Latest message, ${formatDateTime(latest.occurred_at)}` : undefined}>
        {dialog === 'raw' && latest ? <RawMessage messageId={latest.id} /> : null}
      </Drawer>
    </>
  );
}

function LifecycleModal({ conversation: c, reopen, onClose }: { conversation: Conversation; reopen: boolean; onClose: () => void }) {
  const orgId = useOrgId();
  const [reason, setReason] = useState('');
  const [key] = useState(newIdempotencyKey);
  const run = useCommand({
    run: () =>
      unwrap(
        api.POST(reopen ? '/api/v1/orgs/{orgId}/conversations/{conversationId}/reopen' : '/api/v1/orgs/{orgId}/conversations/{conversationId}/close', {
          params: { path: { orgId, conversationId: c.id }, header: { 'If-Match': ifMatch(c.version), 'Idempotency-Key': key } },
          body: { reason: reason.trim() },
        }),
      ),
    success: reopen ? 'Conversation reopened' : 'Conversation closed',
    invalidate: [...conversationKeys(orgId, c.id), orgKey(orgId, 'drafts')],
    onSuccess: onClose,
  });
  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!run.isPending}
      title={reopen ? 'Reopen conversation' : 'Close conversation'}
      description={
        reopen
          ? 'AI drafting stays paused'
          : 'Stops queued AI work and withdraws unsent AI approvals. Late replies still arrive.'
      }
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={run.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={run.isPending} disabled={!reason.trim()} onClick={() => run.mutate(undefined)}>
            {reopen ? 'Reopen conversation' : 'Close conversation'}
          </Button>
        </>
      }
    >
      <Field label="Reason" required>
        <TextArea
          rows={2}
          value={reason}
          maxLength={2000}
          autoFocus
          placeholder={reopen ? 'New offer received' : 'Deal done, or not interested'}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </Modal>
  );
}

function HandoffModal({ location, onClose }: { location: Location; onClose: () => void }) {
  const orgId = useOrgId();
  const [reason, setReason] = useState('');
  const [key] = useState(newIdempotencyKey);
  const run = useCommand({
    run: () =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/handoff', {
          params: {
            path: { orgId, locationId: location.id },
            header: { 'If-Match': ifMatch(location.version), 'Idempotency-Key': key },
          },
          body: { reason: reason.trim(), allow_enrichment: true, notify: true },
        }),
      ),
    success: `${location.name} handed off`,
    invalidate: [orgKey(orgId, 'locations'), orgKey(orgId, 'conversations'), orgKey(orgId, 'drafts')],
    onSuccess: onClose,
  });
  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!run.isPending}
      title={`Hand off ${location.name}`}
      description="AI drafting and sending stop. Unsent AI approvals are withdrawn. Every email is written by hand."
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={run.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={run.isPending} disabled={!reason.trim()} onClick={() => run.mutate(undefined)}>
            Hand off
          </Button>
        </>
      }
    >
      <Field label="Reason" required>
        <TextArea rows={2} value={reason} maxLength={2000} autoFocus onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

function MessageDetails({ messages }: { messages: Message[] }) {
  return (
    <ol className="msg-details">
      {[...messages].reverse().map((m) => {
        const rows: Array<[string, string]> = [
          ['From', addressesOf(m, 'from').map(formatAddress).join(', ') || 'Unknown'],
          ['To', addressesOf(m, 'to').map(formatAddress).join(', ') || 'None'],
        ];
        const cc = addressesOf(m, 'cc');
        if (cc.length) rows.push(['Cc', cc.map(formatAddress).join(', ')]);
        rows.push(['Date', formatDateTime(m.occurred_at)]);
        rows.push(['Source', m.manual_label ? `${ORIGIN[m.record_origin]}: ${m.manual_label}` : ORIGIN[m.record_origin]]);
        if (m.external_send_class) rows.push(['Sent', m.external_send_class === 'human_observed' ? 'Outside Eryce, by a person' : 'Outside Eryce, sender unknown']);
        if (Math.abs(new Date(m.captured_at).getTime() - new Date(m.occurred_at ?? NaN).getTime()) > 60_000)
          rows.push(['Recorded', formatDateTime(m.captured_at)]);
        if (m.rfc_message_id) rows.push(['Message ID', m.rfc_message_id]);
        return (
          <li key={m.id}>
            <p className="strong">{m.subject || 'No subject'}</p>
            <dl className="dl msg-details-dl">
              {rows.map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
              <dt>Record</dt>
              <dd>
                <IdChip id={m.id} label="Message" />
              </dd>
            </dl>
          </li>
        );
      })}
    </ol>
  );
}

function RawMessage({ messageId }: { messageId: string }) {
  const orgId = useOrgId();
  const raw = useQuery({ ...rawMessageQuery(orgId, messageId), retry: false });
  return <QueryState query={raw}>{(text) => <pre className="raw-mime">{text}</pre>}</QueryState>;
}
